// Host payout queue — manual processing for klarna/crypto bookings whose
// pickup has ended and whose host hasn't been paid out yet.
//
// For these payment methods the customer prepaid 100% × (1 + fee_pct) in
// fiat; the host's cash share is arranged manually by finance after
// pickup (SEPA / cash / other rail). "Mark paid" stamps
// `host_payout_completed_at` via the Task 21 endpoint and removes the row
// from the queue on the next refetch.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  DataTable,
  type DataTableColumn,
} from '@drivecars/admin-ui';
import { EmptyHint, PageHeader } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { ApiError } from '@/lib/api';
import { usePager } from '@/lib/use-pager';
import {
  fetchHostPayoutQueue,
  markHostPayoutPaid,
  type HostPayoutQueueRow,
} from '@/lib/admin-api';

const METHOD_COLORS: Record<'klarna' | 'crypto', { bg: string; ink: string }> = {
  klarna: { bg: '#fce7f3', ink: '#9d174d' },
  crypto: { bg: '#fef3c7', ink: '#92400e' },
};

function MethodChip({ method }: { method: 'klarna' | 'crypto' }) {
  const c = METHOD_COLORS[method];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
      style={{ backgroundColor: c.bg, color: c.ink }}
    >
      {method}
    </span>
  );
}

/** Locked-amount-minor → human string. Mirrors BookingDetail.fmtLockedAmount —
 *  divide by 100 (Stripe minor → major), append the ISO currency. */
function fmtAmount(minor: number | null, currency: string | null): string {
  if (minor == null || !currency) return '—';
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

function fmtBid(amount: number | null, currency: string | null): string {
  if (amount == null) return '—';
  return `${amount.toFixed(2)} ${currency ?? ''}`.trim();
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

interface ActionCtx {
  busyRef: string | null;
  onMarkPaid: (row: HostPayoutQueueRow) => void;
}

const buildColumns = (
  ctx: ActionCtx,
): DataTableColumn<HostPayoutQueueRow>[] => [
  {
    key: 'reference',
    h: 'Reference',
    cell: (r) => (
      <Link
        to={`/bookings/${r.reference}` as never}
        className="font-mono text-[12px] text-[color:var(--bo-accent)] hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {r.reference}
      </Link>
    ),
  },
  {
    key: 'method',
    h: 'Method',
    cell: (r) => <MethodChip method={r.paymentMethod} />,
  },
  {
    key: 'customer',
    h: 'Customer',
    cell: (r) => (
      <span className="truncate font-medium">{r.customerName}</span>
    ),
  },
  {
    key: 'vendor',
    h: 'Host',
    cell: (r) => (
      <span className="truncate text-[12.5px]">{r.vendorName}</span>
    ),
  },
  {
    key: 'dateTo',
    h: 'Pickup ended',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtDate(r.dateTo)}
      </span>
    ),
  },
  {
    key: 'paid',
    h: 'Customer paid',
    right: true,
    cell: (r) => (
      <span className="tabular-nums font-semibold">
        {fmtAmount(r.paymentLockedAmountMinor, r.paymentLockedCurrency)}
      </span>
    ),
  },
  {
    key: 'bid',
    h: 'Bid (host portion)',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[12.5px]">
        {fmtBid(r.bidAmountBuy, r.buyCurrency)}
      </span>
    ),
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <RowAction
          kind="primary"
          busy={ctx.busyRef === r.reference}
          onClick={() => ctx.onMarkPaid(r)}
        >
          Mark paid
        </RowAction>
      </div>
    ),
  },
];

export function HostPayoutQueue() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { page, limit, pagination } = usePager();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'host-payout-queue', page, limit],
    queryFn: ({ signal }) => fetchHostPayoutQueue({ page, limit }, signal),
    // Manual processing is slow + low-volume; a 60s poll keeps the page
    // honest without hammering the API.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'host-payout-queue'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  const markPaidMut = useMutation({
    mutationFn: (reference: string) => markHostPayoutPaid(reference),
    onSuccess: invalidate,
  });

  const onMarkPaid = (row: HostPayoutQueueRow) => {
    if (!window.confirm(`Mark host payout paid for ${row.reference}?`)) return;
    markPaidMut.mutate(row.reference);
  };

  const busyRef =
    markPaidMut.isPending && typeof markPaidMut.variables === 'string'
      ? markPaidMut.variables
      : null;

  const cols = buildColumns({ busyRef, onMarkPaid });
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const mutationError = markPaidMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Finance"
        title="Host payouts"
        sub="Klarna and crypto bookings whose pickup has ended and whose host hasn't been paid out yet. Mark paid after arranging the manual payout."
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load queue: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mark-paid failed:{' '}
          {mutationError instanceof ApiError && mutationError.status === 409
            ? 'booking is already paid or not eligible (card bookings settle automatically).'
            : (mutationError as Error).message}
        </div>
      ) : null}
      {!isLoading && total === 0 && !error ? (
        <EmptyHint>
          Queue is empty — no pending host payouts.
        </EmptyHint>
      ) : (
        <DataTable
          columns={cols}
          rows={rows}
          getKey={(r) => r.reference}
          emptyLabel={isLoading ? 'Loading…' : 'No pending host payouts.'}
          onRowClick={(r) =>
            void navigate({ to: `/bookings/${r.reference}` as never })
          }
          pagination={pagination(total)}
        />
      )}
    </div>
  );
}
